import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import { SelectProps } from "@src/api-client/web-api-client";

type SelectOptionsPayload = {
    categoryKey: string;
    selectProps: SelectProps;
};

type AssociatedFieldValuePayload = {
    associatedFieldKey: string;
    associatedFieldValue: string | undefined;
};

type DeletedAssociatedFieldPayload = {
    fieldCategoryKey: string;
    associatedFieldKey: string;
};

export interface ISelectOptions {
    selectOptions: Record<string, SelectProps>;
    isOptionsEmpty: boolean;
    hasOptionsChanges: boolean;
    associatedFieldValues: Record<string, string | undefined>;
    fieldsWithDeletedAssociatedFields: Record<string, string>;
    newSelects: string[];
    isOptionCleared: boolean;
    associatedValue: string;
}

const initialState: ISelectOptions = {
    selectOptions: {},
    isOptionsEmpty: false,
    hasOptionsChanges: false,
    associatedFieldValues: {},
    fieldsWithDeletedAssociatedFields: {},
    newSelects: [],
    isOptionCleared: false,
    associatedValue: "",
};

export const selectOptionsSlice = createSlice({
    name: "selectOptions",
    initialState: initialState,
    reducers: {
        addSelectOptions: (
            state,
            action: PayloadAction<SelectOptionsPayload>,
        ) => {
            const { categoryKey, selectProps } = action.payload;

            state.selectOptions[categoryKey] = new SelectProps({
                associatedFieldKey: "",
                options: [],
            });
            state.selectOptions[categoryKey] = selectProps;
        },
        setIsOptionsEmpty: (state, action: PayloadAction<boolean>) => {
            state.isOptionsEmpty = action.payload;
        },
        setHasOptionsChanges: (state, action: PayloadAction<boolean>) => {
            state.hasOptionsChanges = action.payload;
        },
        addAssociatedFieldValue: (
            state,
            action: PayloadAction<AssociatedFieldValuePayload>,
        ) => {
            const { associatedFieldKey, associatedFieldValue } = action.payload;

            state.associatedFieldValues[associatedFieldKey] =
                associatedFieldValue;
        },
        clearAssociatedFieldValues: (state) => {
            state.associatedFieldValues = {};
        },
        addFieldWithDeletedAssociatedField: (
            state,
            action: PayloadAction<DeletedAssociatedFieldPayload>,
        ) => {
            const { fieldCategoryKey, associatedFieldKey } = action.payload;

            state.fieldsWithDeletedAssociatedFields[fieldCategoryKey] =
                associatedFieldKey;
        },
        removeFieldWithDeletedAssociatedField: (
            state,
            action: PayloadAction<string>,
        ) => {
            const fieldCategoryKey = action.payload;

            delete state.fieldsWithDeletedAssociatedFields[fieldCategoryKey];
        },
        addNewSelect: (state, action: PayloadAction<string>) => {
            const newSelect = action.payload;

            state.newSelects.push(newSelect);
        },
        clearNewSelects: (state) => {
            state.newSelects = [];
        },
        setIsOptionCleared: (state, action: PayloadAction<boolean>) => {
            state.isOptionCleared = action.payload;
        },
        addAssociatedValue: (state, action: PayloadAction<string>) => {
            state.associatedValue = action.payload;
        },
    },
});

export const {
    addSelectOptions,
    setIsOptionsEmpty,
    setHasOptionsChanges,
    addAssociatedFieldValue,
    clearAssociatedFieldValues,
    addFieldWithDeletedAssociatedField,
    removeFieldWithDeletedAssociatedField,
    addNewSelect,
    clearNewSelects,
    setIsOptionCleared,
    addAssociatedValue,
} = selectOptionsSlice.actions;

export default selectOptionsSlice.reducer;
